/**
 * 前端抽奖逻辑模块（纯前端，使用 localStorage 存储）
 *
 * 中奖规则（修订版，2026-08-13）：
 *   1. 特殊奖（唯一奖）：工号 KNI-8251201003 直接中奖 3,000,000 IDR（仅此一份）
 *   2. 普通奖池：50% 概率中奖
 *      中奖后以 2% 概率二次抽奖，抽中则为特殊奖（250,000 IDR，共 20 份）
 *      未中特殊奖则在 [5000, 10000, 20000, 30000, 40000] 中随机选取
 *   3. 普通奖（不含特殊奖 250,000）累计发放总金额 ≤ 72,000,000 IDR
 *   4. 特殊奖 250,000（20 份）不占用 72,000,000 预算
 */

const PRIZE_CONFIG = {
  uniqueEmployeeId: 'KNI-8251201003',     // 唯一奖工号
  uniqueAmount: 3000000,                  // 唯一奖金额（IDR）
  normal: {
    amounts: [5000, 10000, 20000, 30000, 40000],  // 普通奖候选金额（IDR）
    totalCap: 72000000,                             // 普通奖池总金额上限（IDR）
    winProbability: 0.5                             // 普通奖中奖概率
  },
  special: {
    total: 20,          // 特殊奖总份数
    amount: 250000,     // 每份金额（IDR）
    probability: 0.02   // 二次抽奖概率（2%）
  }
}

// localStorage 中使用的 key
const UNIQUE_KEY = 'vote_lottery_unique_claimed'       // 唯一奖是否已领取
const SPECIAL_KEY = 'vote_lottery_special_remaining'   // 特殊奖剩余份数
const NORMAL_TOTAL_KEY = 'vote_lottery_normal_total'   // 普通奖已发放总额
const USER_PREFIX_KEY = 'vote_lottery_user_'           // 用户中奖记录前缀

/**
 * 获取特殊奖剩余份数
 */
function getSpecialRemaining () {
  try {
    const val = localStorage.getItem(SPECIAL_KEY)
    return val !== null ? parseInt(val, 10) : PRIZE_CONFIG.special.total
  } catch (e) {
    return PRIZE_CONFIG.special.total
  }
}

/**
 * 消耗一份特殊奖
 */
function takeSpecialPrize () {
  const remaining = getSpecialRemaining()
  if (remaining <= 0) return false
  try {
    localStorage.setItem(SPECIAL_KEY, String(remaining - 1))
  } catch (e) { /* ignore */ }
  return true
}

/**
 * 获取普通奖已发放总额
 */
function getNormalTotal () {
  try {
    const val = localStorage.getItem(NORMAL_TOTAL_KEY)
    return val !== null ? parseInt(val, 10) : 0
  } catch (e) {
    return 0
  }
}

/**
 * 增加普通奖已发放金额
 */
function addNormalAmount (amount) {
  const total = getNormalTotal() + amount
  try {
    localStorage.setItem(NORMAL_TOTAL_KEY, String(total))
  } catch (e) { /* ignore */ }
  return total
}

/**
 * 保存用户的中奖记录
 */
export function saveUserRecord (userName, result) {
  try {
    localStorage.setItem(USER_PREFIX_KEY + userName, JSON.stringify(result))
  } catch (e) { /* ignore */ }
}

/**
 * 获取用户的中奖记录
 */
export function getUserRecord (userName) {
  try {
    const data = localStorage.getItem(USER_PREFIX_KEY + userName)
    return data ? JSON.parse(data) : null
  } catch (e) {
    return null
  }
}

/**
 * 标记用户的中奖为已核销
 * @param {string} userName
 * @returns {boolean} 是否成功标记
 */
export function claimPrize (userName) {
  try {
    const record = getUserRecord(userName)
    if (!record || !record.won) return false
    record.claimed = true
    localStorage.setItem(USER_PREFIX_KEY + userName, JSON.stringify(record))
    return true
  } catch (e) {
    return false
  }
}

/**
 * 执行抽奖
 *
 * 流程：
 *   1. 如果工号是 KNI-8251201003，直接中特殊奖 3,000,000 IDR（仅此一份）
 *   2. 否则 50% 概率进入中奖流程
 *      a. 以 2% 概率二次抽奖，中则获特殊奖（250,000 IDR，共 20 份）
 *      b. 未中特殊奖则在 [5000, 10000, 20000, 30000, 40000] 中随机选取
 *   3. 普通奖（不含特殊奖）总额不超过 72,000,000 IDR
 *
 * @param {string} employeeId - 用户工号
 * @returns {{ won: boolean, level: string|null, amount: number|null }}
 *   level: 'unique' | 'special' | 'normal' | null
 *   amount: 中奖金额（IDR）| null
 */
export function drawLottery (employeeId) {
  // ---- 1. 特殊奖（唯一奖，工号 KNI-8251201003） ----
  if (employeeId === PRIZE_CONFIG.uniqueEmployeeId) {
    const claimed = localStorage.getItem(UNIQUE_KEY)
    if (!claimed) {
      try {
        localStorage.setItem(UNIQUE_KEY, 'true')
      } catch (e) { /* ignore */ }
      return { won: true, level: 'unique', amount: PRIZE_CONFIG.uniqueAmount }
    }
    // 唯一奖已领取，不再中奖
    return { won: false, level: null, amount: null }
  }

  // ---- 2. 普通奖池（50% 概率） ----
  if (Math.random() < PRIZE_CONFIG.normal.winProbability) {
    // ---- 2a. 二次抽奖：2% 概率中特殊奖（250,000 IDR，20 份，不占用 7200 万预算） ----
    const specialRemaining = getSpecialRemaining()
    if (specialRemaining > 0 && Math.random() < PRIZE_CONFIG.special.probability) {
      takeSpecialPrize()
      return { won: true, level: 'special', amount: PRIZE_CONFIG.special.amount }
    }

    // ---- 2b. 普通奖（5000 ~ 40000 IDR，受 7200 万预算限制） ----
    const normalTotal = getNormalTotal()
    if (normalTotal >= PRIZE_CONFIG.normal.totalCap) {
      // 普通奖池已发完
      return { won: false, level: null, amount: null }
    }

    const amounts = PRIZE_CONFIG.normal.amounts
    const amount = amounts[Math.floor(Math.random() * amounts.length)]

    if (normalTotal + amount > PRIZE_CONFIG.normal.totalCap) {
      return { won: false, level: null, amount: null }
    }

    addNormalAmount(amount)
    return { won: true, level: 'normal', amount }
  }

  // ---- 3. 未中奖 ----
  return { won: false, level: null, amount: null }
}

export { PRIZE_CONFIG }


// WEBPACK FOOTER //
// ./src/utils/lottery.js